import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, inject, HostListener, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import * as d3 from 'd3';
import { Subject, Subscription, timer } from 'rxjs';
import { take, map } from 'rxjs/operators';

class E11NotificationMessage {
    static fromJson(title, message, type, autoClose, ctaText, callback, dismissOnRouteChange = true) {
        return new E11NotificationMessage(title, message, type, autoClose, ctaText, callback, dismissOnRouteChange);
    }
    constructor(title, message, type = 'alert', autoClose = true, ctaText, callback, dismissOnRouteChange = true) {
        this.title = title;
        this.message = message;
        this.type = type;
        this.autoClose = autoClose;
        this.ctaText = ctaText;
        this.callback = callback;
        this.dismissOnRouteChange = dismissOnRouteChange;
        this.ctaText = this.ctaText ?? 'Close';
    }
}

class E11NotificationsService {
    // Observable string streams
    constructor() {
        // Observable sources
        this.notificationMessageSource = new Subject();
        this.callToAction$Source = new Subject();
        this.notificationMessage$ = this.notificationMessageSource.asObservable();
        this.callToAction$ = this.callToAction$Source.asObservable();
    }
    // Service message commands
    popNotificationMessage(message) {
        const notificationMessage = message instanceof E11NotificationMessage ? message : new E11NotificationMessage(message.title, message.message, message.type, message.autoClose, message.ctaText, message.callback, message.dismissOnRouteChange);
        setTimeout(() => {
            this.notificationMessageSource.next(notificationMessage);
        });
    }
    showUnexpectedError(title, message, type = 'alert', ctaText, dismissOnRouteChange) {
        this.popNotificationMessage({
            title: title,
            message: message,
            type: type,
            autoClose: false,
            ctaText: ctaText || 'Close',
            dismissOnRouteChange: dismissOnRouteChange === undefined ? true : dismissOnRouteChange,
        });
    }
    popErrorResponse(errorResponse, unexpectedErrorMessage, ctaText) {
        let error = this.pullErrorMessage(errorResponse);
        if (error) {
            return this.popNotificationMessage(new E11NotificationMessage('An Error Occurred', error, 'danger', false, ctaText || 'Close'));
        }
    }
    initCallToAction(data) {
        this.callToAction$Source.next(data);
    }
    pullErrorMessage(text) {
        if (text.errors) {
            if (text.errors[0]) {
                return text.errors[0].title;
            }
            if (text.errors.summary) {
                return text.errors.summary[0];
            }
        }
        if (text.error) {
            return text.message;
        }
        return typeof text === 'string' ? text : 'undefined';
    }
    dismissAny() {
        this.notificationMessageSource.next(new E11NotificationMessage('', '', '', false, ''));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * Notification Component
 *
 * @example
 * <e11-notifications></e11-notifications>
 */
class E11NotificationsComponent {
    /**
     * Determines if the Notification Component should close when the route changes.
     * @type {boolean}
     * @default false
     */
    set closeOnRouteChange(conditional) {
        this._closeOnRouteChange = conditional !== false;
    }
    constructor(notificationService, elementRef) {
        this.notificationService = notificationService;
        this.elementRef = elementRef;
        /**
         * The unique identifier for the Notification Component.
         * @type {string}
         * @default random string
         * @example 'e11_notification_123456'
         */
        this.id = `e11_notification_${Math.ceil(Math.random() * 1000000).toString()}`;
        /**
         * The Notification Message to display.
         * @type {E11NotificationMessage | null}
         * @default null
         */
        this.notification = null;
        /**
         * Determines if the Notification Component should close when the 'Escape' key is pressed.
         * @type {boolean}
         * @default true
         */
        this.closeOnEscape = true;
        /**
         * Determines if the Notification Component should close when a user clicks outside of the Notification Component.
         * @type {boolean}
         * @default false
         */
        this.closeOnClickOutside = false;
        this._closeOnRouteChange = false;
        this.notificationStatus = false;
        this.notificationCount = 0;
        this.countDown = 5;
        this.counter = 0;
        this.displayCount = 0;
        this.timerSubscription = new Subscription();
        this.messageMaxCharacterLength = 128;
        this.lengthWarning = 'The notification message was rather long. We need to handle these cases...';
        this.isBrowser = inject(IS_BROWSER);
        this.appNoteObservable = new Subscription();
    }
    onKeydownHandler(event) {
        if (this.closeOnEscape) {
            this.close();
        }
    }
    onClick(target) {
        const clickedInside = this.elementRef.nativeElement.contains(target);
        if (!clickedInside && this.closeOnClickOutside) {
            this.close();
        }
    }
    ngOnInit() {
        this.appNoteObservable = this.notificationService.notificationMessage$.subscribe((results) => {
            this.activateNotification(results);
        });
        if (this._closeOnRouteChange) {
            // this.route.events
            //     .pipe(filter((route) => route instanceof NavigationEnd))
            //     .subscribe(() => {
            //         if (this.notification && this.notification.dismissOnRouteChange) {
            //             this.dismissNotificationNoCallback();
            //         }
            //     });
        }
    }
    ngOnDestroy() {
        if (this.appNoteObservable && typeof this.appNoteObservable.unsubscribe === 'function') {
            this.appNoteObservable.unsubscribe();
        }
    }
    /**
     * Closes the Notification Component.
     * @returns {void}
     */
    close() {
        if (this.notificationStatus) {
            this.dismissNotification();
        }
    }
    /**
     * Activates a new Notification Message.  If a Notification Message is already present on-screen
     * it will be dismissed and the new Notification Message will activate after half a second (500ms).
     * @param {E11NotificationMessage} appNotificationMessage - The Notification Message to display.
     * @returns {void}
     */
    activateNotification(appNotificationMessage) {
        if (!this.notification) {
            this.updateNotification(appNotificationMessage);
            return;
        }
        if (this.notification) {
            this.dismissNotificationNoCallback();
            setTimeout(() => {
                this.updateNotification(appNotificationMessage);
            }, 500);
        }
        else {
            this.updateNotification(appNotificationMessage);
        }
    }
    /**
     * Applies the details from an AppNotificationMessage to the NotificationComponent. If the Notification Message
     * is not dismissible a 5 second timer will be created and the notification will auto-dismiss when the timer completes.
     * @param {E11NotificationMessage} appNotificationMessage - The Notification Message to display.
     * @returns {void}
     */
    updateNotification(appNotificationMessage) {
        if (!this.isBrowser)
            return;
        this.notification = appNotificationMessage;
        this.notificationService.initCallToAction('null');
        this.notificationCount++;
        this.notificationStatus = true;
        if (this.notification.autoClose) {
            d3.select('#' + this.id)
                .select('svg')
                .classed('inactive', false);
            d3.select('#' + this.id)
                .select('circle')
                .classed('inactive', false);
            d3.select('#' + this.id)
                .select('svg')
                .classed('active', true);
            d3.select('#' + this.id)
                .select('circle')
                .classed('active', true);
            this.counter = 6;
            this.countDown = timer(0, 1000)
                .pipe(take(this.counter))
                .pipe(map((val) => this.counter - 1 - val));
            this.timerSubscription = this.countDown.subscribe((val) => (this.displayCount = val), (err) => { }, () => this.dismissNotification());
        }
    }
    /**
     * Dismisses a Notification Message. If the Notification Message has a callaback it WILL be in invoked.
     * @returns {void}
     */
    dismissNotification() {
        if (this.notification && this.notification.callback) {
            this.notification.callback();
        }
        this.resetNotificationValues();
    }
    /**
     * Dismisses a Notification Message. If the Notification Message has a callback function it will NOT be invoked.
     * @returns {void}
     */
    dismissNotificationNoCallback() {
        if (this.notification) {
            this.resetNotificationValues();
        }
    }
    /**
     * Resets the Notification Component's properties.  If the notification has a timer and
     * was dismissed before the timer completed then unsubscribe from observable timer.
     * @returns {void}
     */
    resetNotificationValues() {
        if (!this.timerSubscription.closed && this.timerSubscription.unsubscribe) {
            this.timerSubscription.unsubscribe();
        }
        this.notificationStatus = false;
        this.countDown = null;
        this.counter = 0;
        if (this.notification?.autoClose) {
            d3.select('#' + this.id)
                .select('svg')
                .classed('active', false);
            d3.select('#' + this.id)
                .select('circle')
                .classed('active', false);
            d3.select('#' + this.id)
                .select('svg')
                .classed('inactive', true);
            d3.select('#' + this.id)
                .select('circle')
                .classed('inactive', true);
            setTimeout(() => {
                this.notification = null;
            }, 500);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsComponent, deps: [{ token: E11NotificationsService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11NotificationsComponent, isStandalone: true, selector: "e11-notifications", inputs: { id: "id", closeOnRouteChange: "closeOnRouteChange", notification: "notification", closeOnEscape: "closeOnEscape", closeOnClickOutside: "closeOnClickOutside" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event)", "document:click": "onClick($event.target)" } }, ngImport: i0, template: `
    <div
      class="e11-notitifaction e11-border-2 e11-rounded-md e11-p-4 e11-w-11/12 md:e11-w-1/2 lg:e11-w-1/3 xl:e11-w-1/4 e11-shadow-lg"
      id="{{ id }}"
      [ngClass]="{
        active: notificationStatus,
        'e11-border-skin-success e11-bg-green-50': notification?.type === 'success',
        'e11-border-skin-info e11-bg-blue-50': notification?.type === 'info',
        'e11-border-skin-alert e11-bg-yellow-50': notification?.type === 'alert',
        'e11-border-skin-warning e11-bg-orange-50': notification?.type === 'warning',
        'e11-border-skin-danger e11-bg-red-50': notification?.type === 'danger' || notification?.type === 'error'
      }"
    >
      <div class="e11-flex e11-items-start">
        <div class="e11-flex-shrink-0">
          <!-- Heroicon name: outline/check-circle -->
          <span *ngIf="notification?.type === 'success'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-success"> done </span>
          <span *ngIf="notification?.type === 'info'" class="material-icons-outlined e11-h-6 e11-w-6 e11-text-skin-info"> info </span>
          <span *ngIf="notification?.type === 'alert'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-alert"> report_problem </span>
          <span *ngIf="notification?.type === 'warning'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-warning"> error_outline </span>
          <span *ngIf="notification?.type === 'danger'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-danger"> dangerous </span>
          <span *ngIf="notification?.type === 'error'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-error"> error </span>
        </div>
        <div class="e11-ml-3 e11-w-0 e11-flex-1">
          <h4 class="e11-mb-2 e11-mt-[2px] e11-font-medium e11-text-skin-dark e11-cursor-default">
            {{ notification?.title }}
          </h4>
          <p class="e11-text-sm e11-text-skin-base e11-cursor-default message-text">
            {{ notification?.message }}
          </p>
        </div>
        <div class="e11-ml-4 e11-flex-shrink-0 e11-flex e11-items-center e11-justify-center" (click)="dismissNotification()">
          <div
            class="e11-notification-closer-container e11-rounded-full e11-text-medium e11-text-skin-light hover:e11-text-skin-base focus:e11-outline-none e11-transition-all e11-bg-transparent e11-duration-100 e11-border e11-border-transparent hover:e11-border hover:e11-bg-white hover:e11-border-skin-grey e11-cursor-pointer e11-absolute e11-top-2 e11-right-2"
          >
            <span class="e11-sr-only">Close</span>
            <div id="{{ id }}" class="e11-notification-closer e11-relative e11-flex e11-items-center e11-justify-center">
              <svg class="e11-absolute inactive">
                <circle r="17" cx="20" cy="20" class="inactive"></circle>
              </svg>
              <span class="material-icons e11-absolute"> close </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `, isInline: true, styles: [".message-text{word-wrap:break-word}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-notifications', standalone: true, imports: [CommonModule], encapsulation: ViewEncapsulation.None, template: `
    <div
      class="e11-notitifaction e11-border-2 e11-rounded-md e11-p-4 e11-w-11/12 md:e11-w-1/2 lg:e11-w-1/3 xl:e11-w-1/4 e11-shadow-lg"
      id="{{ id }}"
      [ngClass]="{
        active: notificationStatus,
        'e11-border-skin-success e11-bg-green-50': notification?.type === 'success',
        'e11-border-skin-info e11-bg-blue-50': notification?.type === 'info',
        'e11-border-skin-alert e11-bg-yellow-50': notification?.type === 'alert',
        'e11-border-skin-warning e11-bg-orange-50': notification?.type === 'warning',
        'e11-border-skin-danger e11-bg-red-50': notification?.type === 'danger' || notification?.type === 'error'
      }"
    >
      <div class="e11-flex e11-items-start">
        <div class="e11-flex-shrink-0">
          <!-- Heroicon name: outline/check-circle -->
          <span *ngIf="notification?.type === 'success'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-success"> done </span>
          <span *ngIf="notification?.type === 'info'" class="material-icons-outlined e11-h-6 e11-w-6 e11-text-skin-info"> info </span>
          <span *ngIf="notification?.type === 'alert'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-alert"> report_problem </span>
          <span *ngIf="notification?.type === 'warning'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-warning"> error_outline </span>
          <span *ngIf="notification?.type === 'danger'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-danger"> dangerous </span>
          <span *ngIf="notification?.type === 'error'" class="material-icons e11-h-6 e11-w-6 e11-text-skin-error"> error </span>
        </div>
        <div class="e11-ml-3 e11-w-0 e11-flex-1">
          <h4 class="e11-mb-2 e11-mt-[2px] e11-font-medium e11-text-skin-dark e11-cursor-default">
            {{ notification?.title }}
          </h4>
          <p class="e11-text-sm e11-text-skin-base e11-cursor-default message-text">
            {{ notification?.message }}
          </p>
        </div>
        <div class="e11-ml-4 e11-flex-shrink-0 e11-flex e11-items-center e11-justify-center" (click)="dismissNotification()">
          <div
            class="e11-notification-closer-container e11-rounded-full e11-text-medium e11-text-skin-light hover:e11-text-skin-base focus:e11-outline-none e11-transition-all e11-bg-transparent e11-duration-100 e11-border e11-border-transparent hover:e11-border hover:e11-bg-white hover:e11-border-skin-grey e11-cursor-pointer e11-absolute e11-top-2 e11-right-2"
          >
            <span class="e11-sr-only">Close</span>
            <div id="{{ id }}" class="e11-notification-closer e11-relative e11-flex e11-items-center e11-justify-center">
              <svg class="e11-absolute inactive">
                <circle r="17" cx="20" cy="20" class="inactive"></circle>
              </svg>
              <span class="material-icons e11-absolute"> close </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `, styles: [".message-text{word-wrap:break-word}\n"] }]
        }], ctorParameters: () => [{ type: E11NotificationsService }, { type: i0.ElementRef }], propDecorators: { id: [{
                type: Input
            }], closeOnRouteChange: [{
                type: Input
            }], notification: [{
                type: Input
            }], closeOnEscape: [{
                type: Input
            }], closeOnClickOutside: [{
                type: Input
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }] } });

class E11NotificationsModule {
    static forRoot() {
        return {
            ngModule: E11NotificationsModule,
            providers: [], // Be extremely careful about declaring services in providers arrays - it can prevent them from being singletons
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsModule, imports: [E11NotificationsComponent, CommonModule, E11UtilModule], exports: [E11NotificationsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsModule, imports: [E11NotificationsComponent, CommonModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11NotificationsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11NotificationsComponent, CommonModule, E11UtilModule],
                    exports: [E11NotificationsComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11NotificationMessage, E11NotificationsComponent, E11NotificationsModule, E11NotificationsService };
